import { appFetch } from '@/util-react-router/app-fetch';
import { appData } from '@/util-react-router/app-data';
import { buildPageMeta } from '@/util-react-router/build-page-meta';
import {
  Link,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useSubmit,
  useLocation,
  useRouteError,
} from 'react-router';
import { LoginProvider } from '@/util-react-router/providers/login-provider';
import { postFullstorySession } from '@/fullstory/actions/post-fullstory-session';
import { Session } from '@/common/types';
import { setSiftSessionId } from '../@/sift/utils/sift-session-cookie-utils';
import { SiftProvider } from '../@/util-react-router/providers/sift-provider';
import { Toaster } from '@/shadcn-ui/toaster';
import { useDigiohLoginLocalStore } from '@/common/hooks/digioh/use-digioh-login-local-store';
import { useFullstorySetup } from '../@/common/hooks/use-fullstory-setup';
import { useSentryInit } from '@/common/hooks/use-sentry-init';
import { useGTMPageView } from '@/common/hooks/google-tag-manager/use-gtm-page-view';
import {
  UserInformation,
  usePushUserInformationToDataLayer,
} from '@/common/hooks/google-tag-manager/use-push-user-information-to-data-layer';
import adyenCustomStyles from '~/styles/adyen-custom.css?url';
import fontsCustomStyles from '~/styles/fonts-custom.css?url';
import FormLayout from '@/form/components/form-layout';
import logResponseError from '@/util-react-router/log-response-error';
import PageLayout from '@/common/components/page-layout';
import Spacer from '@/common/components/spacer';
import type {
  ActionFunctionArgs,
  LinksFunction,
  LoaderFunctionArgs,
  MetaFunction,
} from 'react-router';
import useReportErrorBoundary from '@/util-react-router/hooks/use-report-error-boundary';
import vaulStyles from '~/styles/vaul-styles.css?url';
import tailwindStyles from '~/styles/tailwind.css?url';
import { useStoreAttribution } from '@/metadata/hooks/use-store-attribution';
import {
  AttributionInformation,
  useGTMAttribution,
} from '@/common/hooks/google-tag-manager/use-gtm-attribution';
import { postAttribution } from '@/metadata/actions/post-attribution';
import { captureException, withSentryReactRouterV7Routing } from '@sentry/react';
import { useAssembledChatScript } from '@/common/hooks/assembled/use-assembled-chat-script';
import {
  FeatureFlagsProvider,
  type FeatureFlags,
} from '@/util-react-router/providers/feature-flags-provider';

export const meta: MetaFunction = () => {
  return buildPageMeta({});
};

export const links: LinksFunction = () => [
  {
    rel: 'stylesheet',
    href: tailwindStyles,
  },
  { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
  { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossOrigin: 'anonymous' },
  {
    rel: 'stylesheet',
    href: 'https://fonts.googleapis.com/css2?family=Inter:wght@100;200;300;400;500;600;700;800;900&display=swap',
  },
  {
    rel: 'stylesheet',
    href: 'https://fonts.googleapis.com/css2?family=Inter+Tight:ital,wght@0,100..900;1,100..900&display=swap',
  },
  {
    rel: 'stylesheet',
    href: 'https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;1,100;1,200;1,300;1,400;1,500;1,600;1,700&display=swap',
  },
  { rel: 'stylesheet', href: fontsCustomStyles },
  { rel: 'stylesheet', href: adyenCustomStyles },
  /**
   * Note: We need to explcitly import Vaul styles for the drawer animation to work.
   * @see https://github.com/emilkowalski/vaul/issues/264
   * @see https://github.com/emilkowalski/vaul/pull/417
   *
   * Also, the style.css file is not being exported correctly by vaul which, means
   * we cannot import it directly here. There're a script in the package.json that
   * copies the styles to a new file that we can import here.
   */
  { rel: 'stylesheet', href: vaulStyles },
  // Apple touch icons for iOS/Safari web app support
  {
    rel: 'apple-touch-icon',
    sizes: '180x180',
    href: '/apple-touch-icon-180x180.png',
  },
  {
    rel: 'apple-touch-icon',
    sizes: '167x167',
    href: '/apple-touch-icon-167x167.png',
  },
  {
    rel: 'apple-touch-icon',
    sizes: '152x152',
    href: '/apple-touch-icon-152x152.png',
  },
  {
    rel: 'apple-touch-icon',
    sizes: '120x120',
    href: '/apple-touch-icon-120x120.png',
  },
  { rel: 'apple-touch-icon', sizes: '76x76', href: '/apple-touch-icon-76x76.png' },
  { rel: 'apple-touch-icon', sizes: '60x60', href: '/apple-touch-icon-60x60.png' },
  { rel: 'apple-touch-icon', href: '/apple-touch-icon.png' }, // fallback, usually 180x180
  // Pre-composed variants to avoid 404s
  {
    rel: 'apple-touch-icon-precomposed',
    sizes: '180x180',
    href: '/apple-touch-icon-180x180-precomposed.png',
  },
  {
    rel: 'apple-touch-icon-precomposed',
    sizes: '167x167',
    href: '/apple-touch-icon-167x167-precomposed.png',
  },
  {
    rel: 'apple-touch-icon-precomposed',
    sizes: '152x152',
    href: '/apple-touch-icon-152x152-precomposed.png',
  },
  {
    rel: 'apple-touch-icon-precomposed',
    sizes: '120x120',
    href: '/apple-touch-icon-120x120-precomposed.png',
  },
  { rel: 'apple-touch-icon-precomposed', href: '/apple-touch-icon-precomposed.png' },
];

export type RootActionResponse = {
  intent?: 'fullstory-session';
  ok?: boolean;
  errors?: { error: string };
  message?: string;
  session?: Session;
};

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData();
  const intent = formData.get('intent');

  switch (intent) {
    case 'fullstory-session': {
      try {
        const { errors, response } = await postFullstorySession(request, formData);

        if (errors) {
          return { errors };
        }

        if (!response?.ok) {
          logResponseError('fullstory-session - action', response);

          return appData({
            data: {
              intent: 'fullstory-session',
              ok: false,
              errors: { error: errors },
            },
            response,
          });
        }

        return appData({
          data: {
            intent: 'fullstory-session',
            ok: true,
            errors: { error: errors },
          },
          response,
        });
      } catch {
        return { errors: { error: 'Failed to link FullStory session' } };
      }
    }

    case 'store-attribution': {
      try {
        const { errors, response } = await postAttribution(request, formData);

        if (errors) {
          return { errors };
        }

        if (!response?.ok) {
          logResponseError('account-attribution - action', response);

          return appData({
            data: {
              intent: 'account-attribution',
              ok: false,
              errors: { error: errors },
            },
            response,
          });
        }

        return appData({
          data: {
            intent: 'account-attribution',
            ok: true,
            errors: { error: errors },
          },
          response,
        });
      } catch {
        return { errors: { error: 'Failed to send attribution info to account' } };
      }
    }

    default: {
      return { errors: { pageError: 'Invalid intent' } };
    }
  }
}

type RootLoaderData = {
  user_signed_in: boolean;
  account_id: string;
  attribution_id: string;
  ASSEMBLED_ENABLED: boolean;
  FULLSTORY_ORG_ID?: string;
  GA_TRACKING_ID?: string;
  PUBLIC_BACKEND_URL?: string;
  SENTRY_DSN?: string;
  SENTRY_ENVIRONMENT?: string;
  SENTRY_RELEASE?: string;
  SIFT_BEACON_KEY?: string;
  siftSessionId: string;
  session: Session;
  metadata?: UserInformation;
  attribution_data?: AttributionInformation;
  DISABLE_SPA_NAVIGATION?: boolean;
};

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const {
    ASSEMBLED_ENABLED,
    FULLSTORY_ORG_ID,
    GA_TRACKING_ID,
    PUBLIC_BACKEND_URL,
    SENTRY_DSN,
    SENTRY_ENVIRONMENT,
    SENTRY_RELEASE,
    SIFT_BEACON_KEY,
    DISABLE_SPA_NAVIGATION,
  } = process.env;

  const config = {
    ASSEMBLED_ENABLED: ASSEMBLED_ENABLED === 'true',
    FULLSTORY_ORG_ID,
    GA_TRACKING_ID,
    PUBLIC_BACKEND_URL,
    SENTRY_DSN,
    SENTRY_ENVIRONMENT,
    SENTRY_RELEASE,
    SIFT_BEACON_KEY,
    user_signed_in: false,
    isLoggedIn: false,
    DISABLE_SPA_NAVIGATION,
  };

  try {
    const { siftSessionId, siftSessionHeaders } = await setSiftSessionId(request);

    const [sessionResponse, metadataResponse, attributionResponse] = await Promise.all([
      appFetch({ url: '/api/external/v1/session', request }),
      appFetch({ url: '/api/external/v1/metadata/hashed_data', request }),
      appFetch({ url: '/api/external/v1/metadata/attribution', request }),
    ]);

    if (!sessionResponse.ok) {
      logResponseError('root - session - loader', sessionResponse);

      return { config };
    }
    const { session } = await sessionResponse.json();

    if (!metadataResponse.ok) {
      logResponseError('root - metadata - loader', metadataResponse);
      // Object.assign is used (instead of spread operator) because the
      // flexcar/no-direct-json rule expects all props in inline object
      // declarations to have a name, so passing an object built with the
      // spread operator causes lint to fail even when the rule is disabled.
      return Object.assign({}, config, {
        user_signed_in: session?.user_signed_in ?? false,
        account_id: session?.account_id,
        isLoggedIn: false,
        siftSessionId,
      });
    }
    const { metadata } = await metadataResponse.json();

    if (!attributionResponse.ok) {
      logResponseError('root - attribution - loader', attributionResponse);
      return Object.assign({}, config, {
        user_signed_in: session?.user_signed_in ?? false,
        account_id: session?.account_id,
        metadata,
        siftSessionId,
      });
    }
    const { attribution_data } = await attributionResponse.json();

    return appData({
      data: {
        ...config,
        user_signed_in: session?.user_signed_in ?? false,
        account_id: session?.account_id,
        siftSessionId,
        metadata,
        attribution_data,
      },
      response: sessionResponse,
      init: { headers: siftSessionHeaders },
    });
  } catch {
    return config;
  }
};

export function App() {
  const {
    user_signed_in,
    account_id,
    ASSEMBLED_ENABLED,
    FULLSTORY_ORG_ID,
    GA_TRACKING_ID,
    SENTRY_DSN,
    SENTRY_ENVIRONMENT,
    SENTRY_RELEASE,
    SIFT_BEACON_KEY,
    siftSessionId,
    metadata,
    attribution_data,
    DISABLE_SPA_NAVIGATION,
  } = useLoaderData<RootLoaderData>();

  const submit = useSubmit();
  const location = useLocation();

  useSentryInit({
    dsn: SENTRY_DSN,
    environment: SENTRY_ENVIRONMENT,
    release: SENTRY_RELEASE,
    account_id: user_signed_in ? account_id : undefined,
  });
  useFullstorySetup({
    user_signed_in,
    account_id,
    FULLSTORY_ORG_ID,
    submit,
    location,
  });
  useStoreAttribution({ location, submit, user_signed_in });
  usePushUserInformationToDataLayer(metadata ?? {});
  useGTMAttribution(attribution_data);
  useGTMPageView(location);
  useDigiohLoginLocalStore(user_signed_in);

  useAssembledChatScript({ user_signed_in, ASSEMBLED_ENABLED });

  return (
    <html lang="en">
      <head>
        {GA_TRACKING_ID ? (
          <script
            dangerouslySetInnerHTML={{
              __html: `
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${GA_TRACKING_ID}');
        `,
            }}
          />
        ) : null}

        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />

        <Meta />
        <Links />
      </head>

      <body>
        {GA_TRACKING_ID ? (
          <noscript>
            <iframe
              title="Google Tag Manager"
              src="https://www.googletagmanager.com/ns.html?id=GTM-K9K9VHRK&gtm_auth=&gtm_preview=&gtm_cookies_win=x"
              height="0"
              width="0"
              style={{ display: 'none', visibility: 'hidden' }}
            ></iframe>
          </noscript>
        ) : null}

        <SiftProvider
          SIFT_BEACON_KEY={SIFT_BEACON_KEY}
          siftSessionId={siftSessionId}
          accountId={account_id}
        >
          <LoginProvider value={user_signed_in}>
            <FeatureFlagsProvider
              flags={{ DISABLE_SPA_NAVIGATION: DISABLE_SPA_NAVIGATION ?? true }}
            >
              <Outlet />
            </FeatureFlagsProvider>
          </LoginProvider>
        </SiftProvider>

        <ScrollRestoration />
        <Toaster />
        <Scripts />
      </body>
    </html>
  );
}

// See https://docs.sentry.io/platforms/javascript/guides/react/features/react-router/v7/#usage-with-routes--component
export default withSentryReactRouterV7Routing(App);

export function ErrorBoundary() {
  const error = useRouteError();
  useReportErrorBoundary();

  // See https://docs.sentry.io/platforms/javascript/guides/react/features/react-router/v7/#custom-error-boundaries
  captureException(error);

  const defaultFeatureFlags: FeatureFlags = {
    DISABLE_SPA_NAVIGATION: true,
  };

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>Error</title>
        <Meta />
        <Links />
      </head>
      <body>
        <FeatureFlagsProvider flags={defaultFeatureFlags}>
          <PageLayout>
            <FormLayout>
              <div className="flex flex-col items-center">
                <h1>Page Not Found</h1>
                <Spacer variant="height24" />
                <p className="body">
                  Whoops! We can&apos;t seem to find the page you&apos;re trying to get
                  to. Please check the URL and try again. Or head back{' '}
                  <Link to="/" className="link-body" reloadDocument={true}>
                    Home
                  </Link>
                  .
                </p>
                <Spacer variant="height60" />
              </div>
            </FormLayout>
          </PageLayout>
        </FeatureFlagsProvider>
        <Scripts />
      </body>
    </html>
  );
}
